import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import type { UserInfo } from "@/lib/auth";

interface ExtendedUserInfo extends UserInfo {
  isAdmin?: boolean;
  isVerified?: boolean;
  isMaintainer?: boolean;
}

interface AuthContextType {
  user: ExtendedUserInfo | null;
  loading: boolean;
  checkAuth: () => Promise<void>;
  login: (provider: "discord" | "dev") => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ExtendedUserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const checkingRef = useRef(false);

  const checkAuth = useCallback(async () => {
    // Prevent concurrent calls
    if (checkingRef.current) {
      return;
    }
    
    checkingRef.current = true;
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Failed to check authentication:", error);
      setUser(null);
    } finally {
      setLoading(false);
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (provider: "discord" | "dev") => {
    if (provider === "dev") {
      window.location.href = `/api/auth/dev`;
    } else {
      window.location.href = `/api/auth/${provider}`;
    }
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      setUser(null);
      // Force a hard reload to clear any cached state
      window.location.href = "/";
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if the request fails, clear local state and redirect
      setUser(null);
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

